'use client';

import { useMutation } from '@apollo/client/react';
import { isApolloError, getErrorCauseMessage } from '@/src/utils/error-handler';
import { PortalModal, usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import { GOD_GENERATION_IMAGE } from '@/.graphql';
import { Avatars } from '@/src/components/Avatars';
import { GenerateButton } from '@/src/components/GenerateButton';
import { Header } from '@/src/components/Header';
import { PromptArea } from '@/src/components/PromptArea';
import {
  useStyleContext,
  useGTMContext,
  useProfileContext,
  useMobileContext,
} from '@/src/providers';

import { AspectRatio } from '../_generate_image/AspectRatio';
import { CloseAppButton } from '../_generate_image/CloseAppButton';

const styles = {
  portal: '!h-dvh !max-h-dvh !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center bg-fill-menu dark:bg-fill-menu bg-fill-menuBg',
  wrapper: 'shrink-0 bg-fill-menu p-5 pt-16',
  title: 'relative text-2xl font-bold mb-4 mt-2 text-center',
  info: 'flex flex-col gap-4 p-4 my-4 bg-fill-quinary rounded-xl',
  infoTitle: 'text-sm font-bold mb-3',
  infoList: 'list-disc pl-4',
  infoListItem: 'text-sm font-medium text-label-secondary',
};

export const GodModePortal: FC = () => {
  const t = useTranslations('imageModal');
  const {
    genderMode,
    isAvatarExist,
    activeAvatar,
    activeAspectRatio,
    profileAvatars,
    prevPortal,
  } = useStyleContext();
  const sendGTM = useGTMContext();
  const { closePortal, openPortal } = usePortalContext();
  const profile = useProfileContext()!;
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [godGeneratonImage, { loading }] = useMutation(GOD_GENERATION_IMAGE);
  const [prompt, setPrompt] = useState<string>('');
  const { isMobiles } = useMobileContext();

  const handleClickGenerate = async () => {
    try {
      setButtonDisabled(true);

      sendGTM({
        event: 'Generate',
        type: null,
        block: 'Режим бога',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: null,
        getSource: 'Меню',
        gender: genderMode,
      });

      const { data } = await godGeneratonImage({
        variables: {
          loraId: activeAvatar,
          resolution: activeAspectRatio,
          prompt,
        },
      });

      if (!data) {
        throw new Error('No data');
      }

      const queueTime = data.godGeneratonImage.queueTime;

      toast(
        <span>
          {t.rich('successMessage', {
            queueTime,
            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
          })}
        </span>,
        {
          position: 'bottom-center',
          icon: (
            <Image
              width={16}
              height={18}
              src="/icons/check.svg"
              alt="check"
              className="w-4 h-4"
            />
          ),
          theme: 'dark',
          style: {
            backgroundColor: '#3D3E41',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '500',
            borderRadius: '12px',
            padding: '16px',
            bottom: '32px',
            maxWidth: 'calc(100% - 40px)',
            alignItems: 'start',
          },
        },
      );

      sendGTM({
        event: 'Generation_complete',
        type: null,
        block: 'Режим бога',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: null,
        getSource: 'Меню',
        gender: genderMode,
      });

      setTimeout(() => {
        setButtonDisabled(false);
      }, 5000);
    } catch (err) {
      const error = err as Error;

      if (isApolloError(error)) {
        const causeMessage = getErrorCauseMessage(error);
        if (causeMessage === 'not_enough_image_generations') {
          closePortal();
          openPortal(PORTALS.TopUpBalance);
        } else {
          toast.error(causeMessage || error.message);
        }
      } else {
        console.error(error);
      }

      sendGTM({
        event: 'Generation_error',
        type: null,
        block: 'Режим бога',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: null,
        getSource: 'Меню',
        gender: genderMode,
      });

      setTimeout(() => {
        setButtonDisabled(false);
      }, 5000);
    }
  };

  const handleClosePortal = () => {
    if (prevPortal === 'allTools') {
      closePortal();
      openPortal(PORTALS.AllTools);
    } else {
      closePortal();
    }
  };

  const disabled = buttonDisabled || loading;

  return (
    <PortalModal portalId={PORTALS.GodMode} className={styles.portal} noClose>
      <div className="h-full relative">
        <div className={twMerge(styles.head, isMobiles && 'pt-[100px]')}>
          <Header isCloseButton onClickCloseButton={handleClosePortal} />
        </div>
        <div
          className={twMerge(
            'h-full overflow-y-auto',
            isMobiles && 'pt-[100px]',
          )}
        >
          <div className={styles.wrapper}>
            <div className={styles.title}>{t('godModeTitle')}</div>
            <PromptArea
              changePrompt={setPrompt}
              prompt={prompt}
              placeholder={t('godModePlaceholder')}
              maxLength={2500}
              className="!bg-fill-quinary !text-sm !font-normal placeholder:!leading-6 !rounded-xl !min-h-[208px] !h-[208px] !border-yellow"
            />
            <div className={styles.info}>
              <Avatars avatars={profileAvatars} />
              <div>
                <div className={styles.infoTitle}>{t('info.title')}</div>
                <ul className={styles.infoList}>
                  <li className={styles.infoListItem}>{t('info.text')}</li>
                </ul>
              </div>
            </div>

            <AspectRatio />

            {profile?.isAcitveStartFunnel ? (
              <CloseAppButton
                text={t('buttonPay')}
                keybord="paymentDownSale4"
              />
            ) : profileAvatars?.length ? (
              <GenerateButton
                disabled={disabled || !isAvatarExist || !prompt}
                onClick={handleClickGenerate}
                text={t('buttonGodMode')}
                price="2"
              />
            ) : (
              <CloseAppButton text={t('buttonAvatar')} keybord="createAvatar" />
            )}
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
